import UpdateIcon from '@mui/icons-material/Update';
import { Paper, Tooltip, Typography } from '@mui/material';
import { MUIDataTableOptions } from 'mui-datatables';
import React, { useState } from 'react';
import { formatDollars, formatNumberCommaSeparated, formatPercentage } from '../../../../../../common';
import { ClientSideDataTable } from '../../../../../../common/components/dataTable/clientSide/clientSideDataTable';
import { DatatableColumn, FilterDataType } from '../../../../../../common/components/dataTable/types';
import { getValueColor, TABLE_PAPER_ELEVATION, TABLE_PAPER_SX, getTableRowProps } from '../../../../../../common/components/dataTable/tableStyles';
import { LoadingProgress } from '../../../../../../common/store/types';
import { AssetGroup, AssetGroupItem, PortfolioParameters } from './../store/types';

export interface Props {
  progress: LoadingProgress;
  data: AssetGroupItem[];
  assetClassColor: string;
  showSecurityPieChart: (security: AssetGroupItem) => void;
  parameters: PortfolioParameters;
  assetClassTitle: string;
  selectedAssetGroup: AssetGroup | null;
  colorIndex: number;
}

export function SecurityListTableCompact({ data, progress, showSecurityPieChart }: Props): JSX.Element {
  const [, setSelectedSecurity] = useState<AssetGroupItem | null>(null);

  const selectSecurity = (security: AssetGroupItem) => {
    document.getElementById('groupByContainer')?.scrollIntoView();
    setSelectedSecurity(security);
    showSecurityPieChart(security);
  };

  const customTableHeaderOptions = {
    sort: true,
  };

  const columns: DatatableColumn[] = [
    {
      filterDataType: FilterDataType.string,
      label: 'NAME',
      name: 'securityCode',
      options: {
        ...customTableHeaderOptions,
        customBodyRenderLite: function customBodyRenderLite(dataIndex: number): React.ReactNode {
          return (
            <div
              onClick={() => selectSecurity(data[dataIndex])}
              style={{ width: '180px' }}
            >
              <Typography
                color={'primary'}
                style={{
                  fontWeight: 'bold',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                }}
              >
                {data[dataIndex]?.securityCode}
              </Typography>
              <Typography color={'textSecondary'} variant={'h6'}>
                {data[dataIndex]?.securityName}
              </Typography>
            </div>
          );
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      name: 'units',
      label: 'UNITS / PRICE',
      textAlign: 'right',
      options: {
        ...customTableHeaderOptions,
        customBodyRenderLite: function customBodyRenderLite(dataIndex: number): React.ReactNode {
          return (
            <div
              onClick={() => selectSecurity(data[dataIndex])}
              style={{ width: '90px' }}
            >
              <Typography align="right">
                {formatNumberCommaSeparated(data[dataIndex]?.units, 4)}
              </Typography>
              <Typography color={'textSecondary'} variant={'h6'} align="right">
                {formatDollars(data[dataIndex]?.unitPrice, false, 4)}
              </Typography>
            </div>
          );
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      textAlign: 'right',
      name: 'currentValue',
      label: 'CURRENT',
      options: {
        ...customTableHeaderOptions,
        customBodyRenderLite: function customBodyRenderLite(dataIndex: number): React.ReactNode {
          return (
            <div
              onClick={() => selectSecurity(data[dataIndex])}
              style={{ width: '90px' }}
            >
              <Typography align="right">
                {formatDollars(data[dataIndex]?.currentValue)}
              </Typography>
              <Typography color={'textSecondary'} variant={'h6'} align="right">
                {formatPercentage(data[dataIndex]?.currentWeightPercentage)}
              </Typography>
            </div>
          );
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      textAlign: 'right',
      name: 'targetValue',
      label: 'TARGET',
      options: {
        ...customTableHeaderOptions,
        customBodyRenderLite: function customBodyRenderLite(dataIndex: number): React.ReactNode {
          return (
            <div
              onClick={() => selectSecurity(data[dataIndex])}
              style={{ width: '90px' }}
            >
              <Typography align="right">
                {formatDollars(data[dataIndex]?.targetValue)}
              </Typography>
              <Typography color={'textSecondary'} variant={'h6'} align="right">
                {formatPercentage(data[dataIndex]?.targetWeightPercentage)}
              </Typography>
            </div>
          );
        },
      },
    },
    {
      filterDataType: FilterDataType.numeric,
      textAlign: 'right',
      name: 'differenceValue',
      label: 'DIFFERENCE',
      options: {
        ...customTableHeaderOptions,
        customBodyRenderLite: function customBodyRenderLite(dataIndex: number): React.ReactNode {
          let unsettledTooltipText = '';
          const buyOrSell = (data[dataIndex]?.unsettledUnits ?? 0) > 0 ? 'Buy' : 'Sale';
          if (data[dataIndex]?.assetClass !== 'Australian Cash') {
            unsettledTooltipText = `Unsettled ${buyOrSell} of ${formatNumberCommaSeparated(
              Math.abs(data[dataIndex]?.unsettledUnits ?? 0)
            )} x ${data[dataIndex]?.securityCode} for ${formatDollars(Math.abs(data[dataIndex]?.unsettledValue ?? 0))}`;
          } else {
            unsettledTooltipText = `Pending settlements of ${formatNumberCommaSeparated(
              Math.abs(data[dataIndex]?.unsettledUnits ?? 0)
            )} x ${data[dataIndex]?.securityCode}`;
          }

          return (
            <>
              <div
                onClick={() => selectSecurity(data[dataIndex])}
                style={{ width: '90px' }}
              >
                <Typography
                  align="right"
                  color={getValueColor(data[dataIndex]?.differenceValue)}
                >
                  {formatDollars(data[dataIndex]?.differenceValue)}
                </Typography>
                <Typography color={'textSecondary'} variant={'h6'} align="right">
                  {formatPercentage(data[dataIndex]?.differenceWeightPercentage)}
                </Typography>
              </div>
              {!!data[dataIndex]?.unsettledValue && (
                <Tooltip title={unsettledTooltipText} aria-label="info">
                  <UpdateIcon color={'primary'} style={{ alignSelf: 'center', fontSize: '20px', paddingLeft: '2px' }} />
                </Tooltip>
              )}
            </>
          );
        },
      },
    },
  ];

  const options: MUIDataTableOptions = {
    download: false,
    filter: false,
    pagination: false,
    print: false,
    search: false,
    selectableRows: 'none',
    selectToolbarPlacement: 'none',
    viewColumns: false,
    setRowProps: getTableRowProps,
  };

  return (
    <Paper elevation={TABLE_PAPER_ELEVATION} sx={TABLE_PAPER_SX}>
      <ClientSideDataTable
        data={data}
        columns={columns}
        options={options}
        loadingProgress={progress}
        id="holdings-datatable"
      />
    </Paper>
  );
}
